import { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const horaires = [
  { jour: 'Lundi', heures: '09:00 - 19:00' },
  { jour: 'Mardi', heures: '09:00 - 19:00' },
  { jour: 'Mercredi', heures: '09:00 - 19:00' },
  { jour: 'Jeudi', heures: '09:00 - 19:00' },
  { jour: 'Vendredi', heures: '09:00 - 19:00' },
  { jour: 'Samedi', heures: '10:00 - 18:00' },
  { jour: 'Dimanche', heures: 'Fermé', closed: true },
];

const DEST_ADDRESS = '8 Rue du Champ de la Gaule, 10120 Saint-Pouange';

interface Suggestion {
  label: string;
}

const NousTrouver = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAllHours, setShowAllHours] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const today = new Date().getDay();
  const todayIndex = today === 0 ? 6 : today - 1;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const searchAddress = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value)}&limit=5`
        );
        const data = await res.json();
        const results = data.features?.map((f: any) => ({ label: f.properties.label })) ?? [];
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const selectSuggestion = (s: Suggestion) => {
    setQuery(s.label);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleCalculer = () => {
    const origin = encodeURIComponent(query || 'Ma position');
    const dest = encodeURIComponent(DEST_ADDRESS);
    window.open(`https://www.google.com/maps/dir/${origin}/${dest}`, '_blank');
  };

  return (
    <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">

          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col"
          >
            {/* Title with bouncing pin */}
            <div className="flex items-center gap-3 mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Nous trouver</h2>
              <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}>
                <MapPin size={28} className="text-primary" />
              </motion.div>
            </div>

            {/* Search itinerary */}
            <div className="mb-10">
              <p className="text-sm text-muted-foreground mb-4">Voir l'itinéraire depuis :</p>
              <div ref={wrapperRef} className="relative">
                <div className="flex items-stretch">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => searchAddress(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="POINT DE DÉPART"
                    className="flex-1 rounded-l-lg border border-r-0 border-border bg-card px-5 py-4 text-sm text-foreground placeholder:text-muted-foreground/40 placeholder:uppercase placeholder:tracking-wider focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                  <button
                    onClick={handleCalculer}
                    className="rounded-r-lg bg-primary px-8 py-4 text-sm font-heading font-bold text-primary-foreground uppercase tracking-[0.15em] hover:bg-primary/90 transition-colors"
                  >
                    Calculer
                  </button>
                </div>
                <AnimatePresence>
                  {showSuggestions && suggestions.length > 0 && (
                    <motion.ul
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-50 left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden"
                    >
                      {suggestions.map((s, i) => (
                        <li key={i}>
                          <button
                            onClick={() => selectSuggestion(s)}
                            className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm text-foreground transition-colors hover:bg-secondary/50"
                          >
                            <MapPin size={14} className="text-muted-foreground shrink-0" />
                            {s.label}
                          </button>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Horaires */}
            <div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-6">Nos horaires</h3>

              {/* Desktop: all hours */}
              <div className="hidden lg:block">
                {horaires.map((h, i) => {
                  const isToday = i === todayIndex;
                  return (
                    <div
                      key={h.jour}
                      className={`flex items-center justify-between py-3.5 px-5 text-sm ${
                        isToday
                          ? 'bg-primary/15 rounded-lg my-0.5'
                          : h.closed
                            ? ''
                            : 'border-b border-border/30'
                      }`}
                    >
                      <span
                        className={`font-heading ${
                          isToday
                            ? 'text-primary font-semibold'
                            : h.closed
                              ? 'text-muted-foreground font-medium'
                              : 'text-foreground font-medium'
                        }`}
                      >
                        {h.jour}{isToday ? ' (aujourd\'hui)' : ''}
                      </span>
                      <span
                        className={`font-heading ${
                          isToday
                            ? 'text-primary font-semibold'
                            : h.closed
                              ? 'text-muted-foreground font-medium'
                              : 'text-foreground font-medium'
                        }`}
                      >
                        {h.heures}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: today + expand */}
              <div className="lg:hidden">
                <div className="flex items-center justify-between py-4 px-5 rounded-lg bg-primary/15 text-sm">
                  <span className="font-heading font-semibold text-primary">
                    {horaires[todayIndex].jour} (aujourd'hui)
                  </span>
                  <span className="font-heading font-semibold text-primary">
                    {horaires[todayIndex].heures}
                  </span>
                </div>

                <button
                  onClick={() => setShowAllHours(!showAllHours)}
                  className="mt-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-heading"
                >
                  {showAllHours ? 'Masquer' : 'Voir plus'}
                  <motion.div animate={{ rotate: showAllHours ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown size={14} />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {showAllHours && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2">
                        {horaires.map((h, i) => {
                          if (i === todayIndex) return null;
                          return (
                            <div
                              key={h.jour}
                              className={`flex items-center justify-between py-3.5 px-5 text-sm ${
                                h.closed ? '' : 'border-b border-border/30'
                              }`}
                            >
                              <span className={`font-heading font-medium ${h.closed ? 'text-muted-foreground' : 'text-foreground'}`}>
                                {h.jour}
                              </span>
                              <span className={`font-heading font-medium ${h.closed ? 'text-muted-foreground' : 'text-foreground'}`}>
                                {h.heures}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Right column: Google Maps */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="min-h-[400px] lg:min-h-[550px] rounded-2xl overflow-hidden shadow-sm"
          >
            <iframe
              src="https://maps.google.com/maps?q=CYgnature+Production+Saint-Pouange&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full min-h-[400px] lg:min-h-[550px]"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              title="Cygnature Production — 8 Rue du Champ de la Gaule, Saint-Pouange"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default NousTrouver;
